'use client';

import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';

// These are code-split with next/dynamic (a marketing visitor never downloads the
// app-provider bundle) but MUST stay server-rendered — i.e. the default ssr: true.
//
// Pass NEITHER `ssr: false` NOR `loading` here. next/dynamic decides
// `hasSuspenseBoundary = !opts.ssr || !!opts.loading`, so either option wraps this
// component — and therefore every page — in a Suspense boundary. That is what blanked
// the entire <body> on the server: no headings, internal links or per-page JSON-LD
// reached the served HTML, and the whole site was de-indexed. The boundary also
// silently swallowed the useSearchParams() prerender bailout, so removing it makes
// any unwrapped useSearchParams() call throw instead.
//
// Note what makes server rendering safe here: not that the providers guard their
// window/document access — a `typeof window` guard that returns a different value on
// the server than on the client's first render is still a hydration mismatch — but
// that the server and the first client render produce identical output. Anything
// mounted below this point has to preserve that property.
const MarketingIntercomProvider = dynamic(() =>
  import('@/layers/presentation/context/IntercomContext').then(m => m.MarketingIntercomProvider),
);

const AppProviders = dynamic(() =>
  import('@/layers/presentation/providers/AppProviders').then(m => m.AppProviders),
);

export function Providers({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const isAppRoute =
    pathname.startsWith('/patient') ||
    pathname.startsWith('/provider') ||
    pathname.startsWith('/sponsor') ||
    pathname.startsWith('/users') ||
    pathname.startsWith('/admin');

  if (!isAppRoute) {
    return <MarketingIntercomProvider>{children}</MarketingIntercomProvider>;
  }

  return <AppProviders>{children}</AppProviders>;
}
